import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { useLocation } from 'react-router-dom';

import { Alert, Box, Button, CircularProgress, Container, Divider, Stack, TextField, Typography } from '@mui/material';

import useAppNavigation from '~/hooks/useAppNavigation';

import {
  type AspectRatio,
  type GetTemplateSchemaResponse,
  createVideoWithRenderOptions,
  getTemplateSchema,
  resolveSourceUrl,
} from '~/services/VideoGenService';

import AudioTimeline from './AudioTimeline';
import TemplateDataForm from './TemplateDataForm';
import TemplateSelect from './TemplateSelect';
import VideoCropPreview, { type CropResult } from './VideoCropPreview';
import VideoTimeline from './VideoTimeline';

const ASPECT_RATIOS: AspectRatio[] = ['16:9', '4:5', '9:16'];

type TemplateSchema = GetTemplateSchemaResponse['result'] & {
  sourceVideoDuration?: number;
};

function parseVideoIdFromSearch(search: string): string | null {
  const params = qs.parse(search, { ignoreQueryPrefix: true });
  return params.videoId ? String(params.videoId) : null;
}

export default function VideoEditPage() {
  const location = useLocation();
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();

  const videoId = useMemo(() => parseVideoIdFromSearch(location.search), [location.search]);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioStartTime, setAudioStartTime] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const [name, setName] = useState('');
  const [template, setTemplate] = useState('');
  const [templateSchema, setTemplateSchema] = useState<TemplateSchema | null>(null);
  const [templateData, setTemplateData] = useState<Record<string, unknown>>({});
  const [isTemplateDataValid, setIsTemplateDataValid] = useState(false);

  const [cropResults, setCropResults] = useState<Record<AspectRatio, CropResult>>({
    '16:9': { x: 0, y: 0, zoom: 1, croppedAreaPercentages: { x: 0, y: 0, width: 100, height: 100 } },
    '4:5': { x: 0, y: 0, zoom: 1, croppedAreaPercentages: { x: 0, y: 0, width: 100, height: 100 } },
    '9:16': { x: 0, y: 0, zoom: 1, croppedAreaPercentages: { x: 0, y: 0, width: 100, height: 100 } },
  });

  const probeVideoRef = useRef<HTMLVideoElement>(null);
  const probeAudioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!videoId) {
      setLoading(false);
      return;
    }

    resolveSourceUrl(videoId)
      .then((response) => {
        setVideoUrl(response.result.url);
      })
      .catch(() => {
        setError('Failed to resolve source video URL');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [videoId]);

  useEffect(() => {
    if (!template) {
      setTemplateSchema(null);
      setTemplateData({});
      return;
    }

    const fetchSchema = async () => {
      try {
        const response = await getTemplateSchema(template);
        setTemplateSchema(response.result);
        setTemplateData({});
      } catch {
        enqueueSnackbar('Failed to fetch template schema', { variant: 'error' });
        setTemplateSchema(null);
      }
    };

    fetchSchema();
  }, [template, enqueueSnackbar]);

  const handleTemplateDataChange = useCallback((data: Record<string, unknown>) => {
    setTemplateData(data);
  }, []);

  const handleTemplateDataValidityChange = useCallback((valid: boolean) => {
    setIsTemplateDataValid(valid);
  }, []);

  const handleAudioUrlChange = useCallback((url: string | null) => {
    setAudioUrl(url);
    setAudioDuration(0);
    setAudioStartTime(0);
  }, []);

  const handleAudioDurationLoaded = useCallback(() => {
    const audio = probeAudioRef.current;
    if (audio && audio.duration && isFinite(audio.duration)) {
      setAudioDuration(audio.duration);
    }
  }, []);

  const handleDurationLoaded = useCallback(() => {
    const video = probeVideoRef.current;
    if (video && video.duration && isFinite(video.duration)) {
      setVideoDuration(video.duration);
    }
  }, []);

  const handleCropComplete = useCallback((ratio: AspectRatio, result: CropResult) => {
    setCropResults((prev) => ({ ...prev, [ratio]: result }));
  }, []);

  const clipDuration = templateSchema?.sourceVideoDuration ?? 7;

  const isFormComplete = useMemo(() => {
    if (!name || name.trim().length < 2 || name.trim().length > 100) return false;
    if (!template) return false;
    if (!isTemplateDataValid) return false;

    if (templateSchema?.required) {
      const allRequiredFilled = templateSchema.required.every((fieldName: string) => {
        if (fieldName === 'videoId') return true;
        const value = templateData[fieldName];
        if (value === undefined || value === null || value === '') return false;
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
      });
      if (!allRequiredFilled) return false;
    }

    return true;
  }, [name, template, isTemplateDataValid, templateSchema, templateData]);

  const mergedTemplateData = useMemo(() => {
    if (!videoId) return templateData;
    return { ...templateData, videoId };
  }, [templateData, videoId]);

  const handleGenerate = async () => {
    if (!videoId || !isFormComplete) return;

    setIsGenerating(true);
    setError(null);

    try {
      const results = await Promise.all(
        ASPECT_RATIOS.map((ratio) => {
          const area = cropResults[ratio].croppedAreaPercentages;

          return createVideoWithRenderOptions({
            name: `${name} (${ratio})`,
            templateName: template,
            templateData: mergedTemplateData,
            aspectRatio: ratio,
            startTime,
            audioStartTime: audioStartTime > 0 ? audioStartTime : undefined,
            cropX: area.x / 100,
            cropY: area.y / 100,
            cropWidth: area.width / 100,
            cropHeight: area.height / 100,
          });
        }),
      );

      const allSucceeded = results.every((r) => r.status === 201);
      if (allSucceeded) {
        enqueueSnackbar('3 video generation jobs queued successfully', { variant: 'success' });
        push('/marketing/video-gen?view=videos');
      }
    } catch (err: unknown) {
      let message = 'Failed to queue video generation jobs';
      if (err instanceof Error) {
        message = err.message;
      }
      const apiError = err as { errors?: Array<{ message?: string; path?: string }> };
      if (apiError?.errors?.length) {
        const validationMessages = apiError.errors
          .map((e) => e.message?.split('\n')[0] ?? e.path ?? 'Unknown validation error')
          .join('; ');
        message = `Validation failed: ${validationMessages}`;
      }
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  if (!videoId) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">No video ID found. Please start from the Create Video dialog.</Alert>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => push('/marketing/video-gen')}>
            Back to Video Generator
          </Button>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Stack alignItems="center" justifyContent="center" sx={{ mt: 8 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading source video...</Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h5">Edit Video</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={() => push('/marketing/video-gen')}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerate} disabled={isGenerating || !videoUrl || !isFormComplete}>
            {isGenerating ? 'Generating...' : 'Generate 3 Videos'}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3} sx={{ mb: 4, maxWidth: 600 }}>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          label="Video Name *"
          fullWidth
          placeholder="Enter a name for your video"
          helperText="Min length: 2 characters, Max length: 100 characters"
          error={name.trim().length > 0 && (name.trim().length < 2 || name.trim().length > 100)}
        />

        <TemplateSelect value={template} onChange={setTemplate} label="Select Template" />

        {templateSchema && (
          <TemplateDataForm
            schema={templateSchema}
            onDataChange={handleTemplateDataChange}
            onValidityChange={handleTemplateDataValidityChange}
            onAudioUrlChange={handleAudioUrlChange}
            initialData={templateData}
            excludeFields={['videoId']}
          />
        )}

        {template && !templateSchema && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading template schema...
            </Typography>
          </Stack>
        )}

        {!template && (
          <Typography variant="body2" color="text.secondary">
            Select a template to see the required fields.
          </Typography>
        )}
      </Stack>

      {videoUrl && (
        <>
          <Divider sx={{ mb: 3 }} />

          <video
            ref={probeVideoRef}
            src={videoUrl}
            preload="metadata"
            onLoadedMetadata={handleDurationLoaded}
            style={{ display: 'none' }}
          />

          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 3 }}>
            {ASPECT_RATIOS.map((ratio) => (
              <VideoCropPreview
                key={ratio}
                videoUrl={videoUrl}
                aspectRatio={ratio}
                startTime={startTime}
                clipDuration={clipDuration}
                onCropComplete={(result) => handleCropComplete(ratio, result)}
              />
            ))}
          </Stack>

          {videoDuration > 0 && (
            <Box sx={{ maxWidth: 1000, mx: 'auto', mb: 4 }}>
              <VideoTimeline
                videoUrl={videoUrl}
                videoDuration={videoDuration}
                clipDuration={clipDuration}
                startTime={startTime}
                onStartTimeChange={setStartTime}
              />
            </Box>
          )}

          {videoDuration === 0 && (
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading video duration...
                </Typography>
              </Stack>
            </Box>
          )}

          {audioUrl && (
            <>
              <audio
                ref={probeAudioRef}
                src={audioUrl}
                preload="metadata"
                onLoadedMetadata={handleAudioDurationLoaded}
                style={{ display: 'none' }}
              />
              {audioDuration > 0 && (
                <Box sx={{ maxWidth: 1000, mx: 'auto', mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Audio Trim
                  </Typography>
                  <AudioTimeline
                    audioUrl={audioUrl}
                    audioDuration={audioDuration}
                    clipDuration={clipDuration}
                    startTime={audioStartTime}
                    onStartTimeChange={setAudioStartTime}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
}
